import { createColumnHelper } from '@tanstack/react-table'
import { StyleSheet, css } from 'aphrodite'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import ActionMenu from '../components/ActionMenu'
import StatusChip from '../components/StatusChip'
import Table from '../components/dashboard/Table'
import InvoiceTypeModal from '../components/invoice/InvoiceTypeModal'
import Tabs from '../components/ui/Tabs'
import { useUser } from '../contexts/UserContext'
import { invoiceService } from '../services/invoiceService'
import type { Invoice } from '../types/invoice'
import { StakeholderType } from '../types/user'
import { formatDate } from '../utils/date'
import CreateBrokerInvoicePage from './CreateBrokerInvoicePage'
import CreateDriverInvoicePage from './CreateDriverInvoicePage'

const ViewInvoicesPage: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalCount, setTotalCount] = useState<number | undefined>(undefined)
  const [totalPages, setTotalPages] = useState<number | undefined>(undefined)
  const [downloadingId, setDownloadingId] = useState<number | null>(null)
  const [downloadingCsvId, setDownloadingCsvId] = useState<number | null>(null)
  const [updatingStatusId, setUpdatingStatusId] = useState<number | null>(null)
  const [showInvoiceTypeModal, setShowInvoiceTypeModal] = useState(false)
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'paid' | 'unpaid' | 'disputed' | 'canceled'
  >('all')
  const [showDisputeModal, setShowDisputeModal] = useState(false)
  const [disputeInvoiceId, setDisputeInvoiceId] = useState<number | null>(null)
  const [disputeReason, setDisputeReason] = useState('')
  const [isDisputing, setIsDisputing] = useState(false)
  const navigate = useNavigate()
  const { id } = useParams()
  const { user } = useUser()
  const location = useLocation()

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        setIsLoading(true)
        const isReceived = location.pathname.includes('/invoices/received')
        const fetchParams: {
          page: number
          page_size: number
          status?: string
        } = { page, page_size: pageSize }
        if (statusFilter !== 'all') {
          fetchParams.status = statusFilter.toUpperCase()
        }
        const response = isReceived
          ? await invoiceService.received(fetchParams)
          : await invoiceService.list(fetchParams)
        setInvoices(response.items)
        setTotalCount(response.count)
        setTotalPages(response.pages)
      } catch (error) {
        console.error('Failed to fetch invoices:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchInvoices()
  }, [location.pathname, page, pageSize, statusFilter])

  const handleNewInvoice = () => {
    const types = user?.stakeholder_types || []
    const hasSubHauler = types.includes(StakeholderType.SUB_HAULER)
    const hasBroker = types.includes(StakeholderType.BROKER)
    const hasContractorType = types.includes(StakeholderType.CONTRACTOR)

    // Count how many distinct invoice creation paths the user has
    const pathCount =
      (hasSubHauler ? 2 : 0) + // driver invoice + SH-to-SH invoice
      (hasBroker ? 1 : 0) +
      (hasContractorType ? 1 : 0)

    if (pathCount > 1) {
      // Multiple options — show the modal
      setShowInvoiceTypeModal(true)
    } else if (hasSubHauler) {
      navigate('/invoices/create/driver')
    } else if (hasBroker) {
      navigate('/invoices/create/broker')
    } else if (hasContractorType) {
      navigate('/invoices/create/contractor')
    }
  }

  const columnHelper = createColumnHelper<Invoice>()

  const handleDownload = async (invoiceId: number) => {
    setDownloadingId(invoiceId)
    try {
      const pdfBlob = await invoiceService.downloadPdf(invoiceId)
      const url = window.URL.createObjectURL(pdfBlob)
      const link = document.createElement('a')
      link.href = url
      link.download = `invoice-${invoiceId}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading PDF:', error)
      toast.error('Failed to download PDF. Please try again.', {
        style: { color: '#721c24' },
      })
    } finally {
      setDownloadingId(null)
    }
  }

  const handleDownloadCsv = async (invoiceId: number) => {
    setDownloadingCsvId(invoiceId)
    try {
      const csvBlob = await invoiceService.downloadCsv(invoiceId)
      const url = window.URL.createObjectURL(csvBlob)
      const link = document.createElement('a')
      link.href = url
      link.download = `invoice-${invoiceId}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading CSV:', error)
      toast.error('Failed to download CSV. Please try again.', {
        style: { color: '#721c24' },
      })
    } finally {
      setDownloadingCsvId(null)
    }
  }

  const handleMarkAsPaid = async (invoiceId: number) => {
    try {
      setUpdatingStatusId(invoiceId)
      const updatedInvoice = await invoiceService.updateStatus(invoiceId, {
        status: 'PAID',
      })

      // Update the invoice in the local state
      setInvoices((prevInvoices) =>
        prevInvoices.map((invoice) =>
          invoice.id === invoiceId ? updatedInvoice : invoice,
        ),
      )

      toast.success('Invoice marked as paid')
    } catch (error) {
      console.error('Failed to update invoice status:', error)
      toast.error('Failed to update invoice status')
    } finally {
      setUpdatingStatusId(null)
    }
  }

  const handleDispute = async () => {
    if (!disputeInvoiceId || !disputeReason.trim()) return
    setIsDisputing(true)
    try {
      const updated = await invoiceService.disputeInvoice(
        disputeInvoiceId,
        disputeReason.trim(),
      )
      setInvoices((prev) =>
        prev.map((inv) => (inv.id === disputeInvoiceId ? updated : inv)),
      )
      setShowDisputeModal(false)
      setDisputeInvoiceId(null)
      setDisputeReason('')
      toast.success('Invoice disputed successfully')
    } catch (error) {
      console.error('Failed to dispute invoice:', error)
      toast.error('Failed to dispute invoice')
    } finally {
      setIsDisputing(false)
    }
  }

  const isReceived = location.pathname.includes('/invoices/received')

  const columns = [
    columnHelper.display({
      id: 'actions',
      cell: (props) => {
        const invoiceId = props.row.original.id
        const invoice = props.row.original
        const actions = [
          {
            label: 'View Invoice',
            icon: 'fa-solid fa-eye',
            onClick: () => navigate(`/invoices/view/${invoiceId}`),
            className: css(styles.viewButton),
          },
          {
            label: 'Download PDF',
            icon:
              downloadingId === invoiceId
                ? 'fa-solid fa-spinner fa-spin'
                : 'fa-solid fa-download',
            onClick: () => handleDownload(invoiceId),
            disabled: downloadingId === invoiceId,
            className: css(styles.downloadButton),
          },
          {
            label: 'Download CSV',
            icon:
              downloadingCsvId === invoiceId
                ? 'fa-solid fa-spinner fa-spin'
                : 'fa-solid fa-file-csv',
            onClick: () => handleDownloadCsv(invoiceId),
            disabled: downloadingCsvId === invoiceId,
            className: css(styles.downloadButton),
          },
        ]

        // Add Edit action for sent UNPAID invoices (only visible to creator)
        if (!isReceived && invoice.status === 'UNPAID') {
          actions.push({
            label: 'Edit',
            icon: 'fa-solid fa-pen',
            onClick: () => navigate(`/invoices/edit/${invoiceId}`),
            className: css(styles.viewButton),
          })
        }

        // Add Mark as Paid action for received UNPAID invoices (recipient pays)
        if (isReceived && invoice.status === 'UNPAID') {
          actions.push({
            label: 'Mark as Paid',
            icon:
              updatingStatusId === invoiceId
                ? 'fa-solid fa-spinner fa-spin'
                : 'fa-solid fa-check',
            onClick: () => handleMarkAsPaid(invoiceId),
            disabled: updatingStatusId === invoiceId,
            className: css(styles.markPaidButton),
          })
        }

        // Add Dispute action for received UNPAID invoices
        if (isReceived && invoice.status === 'UNPAID') {
          actions.push({
            label: 'Dispute',
            icon: 'fa-solid fa-flag',
            onClick: () => {
              setDisputeInvoiceId(invoiceId)
              setShowDisputeModal(true)
            },
            className: css(styles.disputeButton),
          })
        }

        return <ActionMenu actions={actions} />
      },
      header: () => <span>Actions</span>,
    }),
    columnHelper.accessor('invoice_number', {
      cell: (info) => info.getValue(),
      header: () => <span>INVOICE #</span>,
    }),
    columnHelper.accessor('status', {
      cell: (info) => {
        const status = info.getValue()
        const invoice = info.row.original
        // UNPAID + past due_date = display as OVERDUE
        const isOverdue =
          status === 'UNPAID' &&
          invoice.due_date &&
          new Date(invoice.due_date) < new Date()
        const displayStatus = isOverdue ? 'OVERDUE' : status
        const chipVariant = isOverdue
          ? 'overdue'
          : (status.toLowerCase() as
              | 'unpaid'
              | 'paid'
              | 'canceled'
              | 'disputed')
        return <StatusChip variant={chipVariant}>{displayStatus}</StatusChip>
      },
      header: () => <span>STATUS</span>,
    }),
    columnHelper.accessor(
      (row) => {
        if (isReceived) {
          // For received invoices, show who created/sent the invoice
          // Prefer snapshot, fallback to FK for backward compatibility
          const name = row.created_by_first_name
            ? `${row.created_by_first_name} ${row.created_by_last_name || ''}`.trim()
            : `${row.created_by?.first_name || ''} ${row.created_by?.last_name || ''}`.trim()
          return name || row.created_by_email || row.created_by?.email || '-'
        } else {
          // For sent invoices, show who we're billing
          // Prefer snapshot, fallback to FK for backward compatibility
          const name = row.bill_to_first_name
            ? `${row.bill_to_first_name} ${row.bill_to_last_name || ''}`.trim()
            : `${row.bill_to?.first_name || ''} ${row.bill_to?.last_name || ''}`.trim()
          return name || row.bill_to_email || row.bill_to?.email || '-'
        }
      },
      {
        id: 'bill_to_display',
        cell: (info) => info.getValue(),
        header: () => <span>{isReceived ? 'SENT FROM' : 'BILL TO'}</span>,
      },
    ),
    columnHelper.accessor('invoice_date', {
      cell: (info) => formatDate(info.getValue()),
      header: () => <span>CREATED DATE</span>,
    }),
    columnHelper.accessor('due_date', {
      cell: (info) => formatDate(info.getValue()),
      header: () => <span>DUE DATE</span>,
    }),
    columnHelper.accessor('total_amount', {
      cell: (info) => {
        const val = String(info.getValue() ?? '0')
        const num = Number.parseFloat(val)
        return `$${Number.isFinite(num) ? num.toFixed(2) : '0.00'}`
      },
      header: () => <span>TOTAL AMOUNT</span>,
    }),
  ]

  // Server-side filtering is active, so invoices already match the selected tab
  const invoiceList = Array.isArray(invoices) ? invoices : []

  const sortedInvoices = [...invoiceList].sort((a, b) => {
    const dateA = new Date(a.invoice_date || '')
    const dateB = new Date(b.invoice_date || '')
    return dateB.getTime() - dateA.getTime()
  })

  return (
    <div className={css(styles.invoicesDashboard)}>
      <div className={css(styles.mainContent)}>
        <div className={css(styles.header)}>
          <div className={css(styles.headerLeft)}>
            <h2 className={css(styles.title)}>
              {location.pathname.includes('/invoices/received')
                ? 'Invoices - Accounts Payable'
                : 'Invoices - Accounts Receivable'}
            </h2>
            <Tabs
              tabs={[
                { id: 'all', label: 'All Invoices' },
                { id: 'unpaid', label: 'Unpaid' },
                { id: 'paid', label: 'Paid' },
                { id: 'disputed', label: 'Disputed' },
                { id: 'canceled', label: 'Canceled' },
              ]}
              activeTab={statusFilter}
              onTabChange={(tabId) => {
                setStatusFilter(
                  tabId as 'all' | 'paid' | 'unpaid' | 'disputed' | 'canceled',
                )
                setPage(1)
              }}
            />
          </div>
          {!isReceived && (
            <button
              className={css(styles.newInvoiceButton)}
              onClick={handleNewInvoice}
            >
              <i className="fa-solid fa-plus" style={{ marginRight: 6 }} /> New
              Invoice
            </button>
          )}
        </div>

        <div className={css(styles.tableContainer)}>
          <div className={css(styles.tableWrapper)}>
            {isLoading ? (
              <div className={css(styles.loadingContainer)}>
                <i className="fa-duotone fa-solid fa-spinner-third fa-spin fa-3x" />
                <p className={css(styles.loadingText)}>Loading invoices...</p>
              </div>
            ) : (
              <Table
                data={sortedInvoices}
                columns={columns}
                page={page}
                pages={totalPages}
                count={totalCount}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                  setPageSize(size)
                  setPage(1)
                }}
              />
            )}
          </div>
        </div>
      </div>
      <InvoiceTypeModal
        isOpen={showInvoiceTypeModal}
        onClose={() => setShowInvoiceTypeModal(false)}
        stakeholderTypes={user?.stakeholder_types}
      />

      {/* Dispute modal */}
      {showDisputeModal && (
        <div
          className={css(styles.modalOverlay)}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowDisputeModal(false)
              setDisputeInvoiceId(null)
              setDisputeReason('')
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              setShowDisputeModal(false)
              setDisputeInvoiceId(null)
              setDisputeReason('')
            }
          }}
        >
          <div className={css(styles.modalContent)}>
            <div className={css(styles.modalHeader)}>
              <h3 className={css(styles.modalTitle)}>Dispute Invoice</h3>
              <button
                className={css(styles.modalCloseButton)}
                onClick={() => {
                  setShowDisputeModal(false)
                  setDisputeInvoiceId(null)
                  setDisputeReason('')
                }}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className={css(styles.modalBody)}>
              <p className={css(styles.modalDescription)}>
                Please provide a reason for disputing this invoice. The invoice
                creator will be notified.
              </p>
              <textarea
                className={css(styles.disputeTextarea)}
                placeholder="Enter dispute reason..."
                value={disputeReason}
                onChange={(e) => setDisputeReason(e.target.value)}
                rows={4}
                maxLength={1000}
              />
              <div className={css(styles.charCount)}>
                {disputeReason.length}/1000
              </div>
            </div>
            <div className={css(styles.modalFooter)}>
              <button
                className={css(styles.modalCancelButton)}
                onClick={() => {
                  setShowDisputeModal(false)
                  setDisputeInvoiceId(null)
                  setDisputeReason('')
                }}
              >
                Cancel
              </button>
              <button
                className={css(styles.modalConfirmButton)}
                onClick={handleDispute}
                disabled={!disputeReason.trim() || isDisputing}
              >
                {isDisputing ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin" /> Submitting...
                  </>
                ) : (
                  'Submit Dispute'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const styles = StyleSheet.create({
  invoicesDashboard: {
    display: 'flex',
    padding: '16px',
    backgroundColor: '#F9FAFB',
    minHeight: '100vh',
    width: 'calc(100vw - 280px)',
    boxSizing: 'border-box',
    overflowX: 'hidden',
    '@media (max-width: 1024px)': {
      padding: '12px',
      width: '100vw',
    },
  },
  mainContent: {
    flex: 1,
    width: '100%',
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '20px',
    padding: '20px',
    '@media (max-width: 1024px)': {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: '12px',
      padding: '12px',
    },
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: 600,
  },
  tableContainer: {
    borderRadius: '8px',
    padding: '16px',
    width: '100%',
    boxSizing: 'border-box',
    overflowX: 'hidden',
  },
  tableWrapper: {
    width: '100%',
    overflowX: 'auto',
  },
  downloadButton: {
    backgroundColor: '#10B981',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '6px 10px',
    ':hover': {
      backgroundColor: '#059669',
    },
    ':disabled': {
      backgroundColor: '#6EE7B7',
      cursor: 'not-allowed',
    },
  },
  newInvoiceButton: {
    backgroundColor: '#1C64F2',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    padding: '8px 16px',
    marginLeft: 'auto',
    ':hover': {
      backgroundColor: '#1955CC',
    },
    '@media (max-width: 1024px)': {
      display: 'none',
    },
  },
  viewButton: {
    backgroundColor: '#F3F4F6',
    color: '#374151',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '6px 10px',
    ':hover': {
      backgroundColor: '#E5E7EB',
      color: '#1C64F2',
    },
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '50vh',
    color: '#6B7280',
  },
  loadingText: {
    marginTop: '16px',
    fontSize: '16px',
  },
  markPaidButton: {
    padding: '6px 10px',
    color: '#FFFFFF',
    background: '#059669',
    border: 'none',
    cursor: 'pointer',
    borderRadius: 6,
    fontWeight: 500,
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    maxWidth: '120px',
    ':hover': {
      backgroundColor: '#047857',
    },
    ':disabled': {
      backgroundColor: '#6EE7B7',
      cursor: 'not-allowed',
    },
  },
  disputeButton: {
    padding: '6px 10px',
    color: '#FFFFFF',
    background: '#F59E0B',
    border: 'none',
    cursor: 'pointer',
    borderRadius: 6,
    fontWeight: 500,
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    maxWidth: '120px',
    ':hover': {
      backgroundColor: '#D97706',
    },
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1200,
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '100%',
    maxWidth: '480px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 20px 0 20px',
  },
  modalTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: '#111827',
  },
  modalCloseButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '18px',
    color: '#6B7280',
    padding: '4px',
    ':hover': {
      color: '#111827',
    },
  },
  modalBody: {
    padding: '16px 20px',
  },
  modalDescription: {
    margin: '0 0 12px 0',
    fontSize: '14px',
    color: '#6B7280',
    lineHeight: '1.5',
  },
  disputeTextarea: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '6px',
    border: '1px solid #D1D5DB',
    fontSize: '14px',
    resize: 'vertical',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    ':focus': {
      outline: 'none',
      borderColor: '#3B82F6',
      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
    },
  },
  charCount: {
    textAlign: 'right',
    fontSize: '12px',
    color: '#9CA3AF',
    marginTop: '4px',
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '16px 20px',
    borderTop: '1px solid #E5E7EB',
  },
  modalCancelButton: {
    backgroundColor: '#F3F4F6',
    color: '#374151',
    padding: '10px 20px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    ':hover': {
      backgroundColor: '#E5E7EB',
    },
  },
  modalConfirmButton: {
    backgroundColor: '#F59E0B',
    color: 'white',
    padding: '10px 20px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    ':hover': {
      backgroundColor: '#D97706',
    },
    ':disabled': {
      backgroundColor: '#FCD34D',
      cursor: 'not-allowed',
      color: '#A16207',
    },
  },
})

export default ViewInvoicesPage
